import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Paper,
    Box,
    Stack,
    Typography,
    Chip,
    Divider,
    Button,
    Link,
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import DescriptionIcon from '@mui/icons-material/Description';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { CalendarEvent } from '../types';
import { formatLocation, getGameTeams, getLiveBarnLink } from '../utils/eventUtils';
import { logEvent } from '../analytics';

export interface NextDutyCardProps {
    duty: string;
    event: CalendarEvent;
    daysUntil?: number | null;
}

const formatTimeUntil = (eventDate: Date): string => {
    const now = new Date();
    const diffMs = eventDate.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 0) {
        return 'recently completed';
    } else if (diffHours < 1) {
        return `in ${Math.round(diffHours * 60)} minutes`;
    } else if (diffHours < 48) {
        const hours = Math.round(diffHours);
        return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const days = Math.ceil(diffHours / 24);
    return `in ${days} ${days === 1 ? 'day' : 'days'}`;
};

const getDutyDetails = (duty: string) => {
    const norm = duty.toLowerCase();
    if (norm.includes('scoreboard') || norm.includes('clock')) {
        return {
            icon: <SportsHockeyIcon fontSize="small" />,
            badge: 'Scoreboard Operator',
        };
    }
    if (norm.includes('gamesheet') || norm.includes('score')) {
        return {
            icon: <DescriptionIcon fontSize="small" />,
            badge: 'Official Scorer',
        };
    }
    if (norm.includes('penalty')) {
        return {
            icon: <ShieldOutlinedIcon fontSize="small" />,
            badge: 'Penalty Box Attendant',
        };
    }
    if (norm.includes('music') || norm.includes('audio') || norm.includes('dj')) {
        return {
            icon: <MusicNoteIcon fontSize="small" />,
            badge: 'DJ & Audio',
        };
    }
    return {
        icon: <VolunteerActivismIcon fontSize="small" />,
        badge: 'Volunteer Duty',
    };
};

const NextDutyCard: React.FC<NextDutyCardProps> = ({ duty, event, daysUntil }) => {
    const eventDate = new Date(event.start);
    const gameTeams = getGameTeams(event);
    const isHomeJets = Boolean(gameTeams && /\b(?:jets|csdhl)\b/i.test(gameTeams.homeTeam));
    const isAwayJets = Boolean(gameTeams && /\b(?:jets|csdhl)\b/i.test(gameTeams.awayTeam));
    const isInternalLink = event.url?.startsWith('/');
    const { icon: dutyIcon, badge: dutyBadge } = getDutyDetails(duty);

    return (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <AssignmentIndIcon color="primary" sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem' } }} />
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                fontWeight: 600,
                                color: 'text.primary',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Your Next Duty
                        </Typography>
                    </Stack>
                    <Box sx={{ flexShrink: 0 }}>
                        {daysUntil === 0 ? (
                            <Chip label="Today" color="error" size="small" sx={{ fontWeight: 700 }} />
                        ) : daysUntil === 1 ? (
                            <Chip label="Tomorrow" color="warning" size="small" sx={{ fontWeight: 700 }} />
                        ) : daysUntil !== null && daysUntil !== undefined ? (
                            daysUntil > 0 ? (
                                <Chip
                                    label={`In ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            ) : (
                                <Chip label="Completed" size="small" variant="outlined" />
                            )
                        ) : null}
                    </Box>
                </Stack>
            </Box>

            <Divider />

            {/* Card Body */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Hero Role Banner */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 2,
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        borderColor: 'rgba(25, 118, 210, 0.25)',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                    }}
                >
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: 'uppercase',
                                letterSpacing: 0.8,
                                fontWeight: 700,
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.72rem',
                            }}
                        >
                            Assigned Volunteer Role
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                                mt: 0.25,
                                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                            }}
                        >
                            {duty}
                        </Typography>
                    </Box>
                    <Chip
                        icon={dutyIcon}
                        label={dutyBadge}
                        size="small"
                        color="primary"
                        sx={{
                            fontWeight: 600,
                            bgcolor: 'primary.main',
                            color: 'common.white',
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                </Paper>

                {/* Matchup & Event Summary */}
                {event.tournamentName && (
                    <Chip
                        label={event.tournamentName}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 1, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                    />
                )}

                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                    {event.url ? (
                        isInternalLink ? (
                            <Link
                                component={RouterLink}
                                to={event.url}
                                onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}
                            >
                                {event.summary}
                            </Link>
                        ) : (
                            <Link
                                href={event.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}
                            >
                                {event.summary}
                            </Link>
                        )
                    ) : (
                        event.summary
                    )}
                </Typography>

                {/* HOME / AWAY Matchup Box */}
                {gameTeams && (
                    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, my: 1.25, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                        {/* HOME Team */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                            <Chip
                                label="HOME"
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isHomeJets ? 700 : 500,
                                    color: isHomeJets ? 'primary.main' : 'text.primary',
                                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                }}
                            >
                                {gameTeams.homeTeam}
                            </Typography>
                        </Stack>
                        {/* AWAY Team */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                label="AWAY"
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isAwayJets ? 700 : 500,
                                    color: isAwayJets ? 'primary.main' : 'text.primary',
                                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                }}
                            >
                                {gameTeams.awayTeam}
                            </Typography>
                        </Stack>
                    </Paper>
                )}

                {/* Date, Time & Location Stack */}
                <Stack spacing={0.85} sx={{ mt: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EventIcon sx={{ fontSize: '1.1rem', color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                            <strong style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                {eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'America/Chicago' })}
                            </strong>
                            {' at '}
                            {eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}
                            {' '}
                            <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>({formatTimeUntil(eventDate)})</span>
                        </Typography>
                    </Stack>
                    {event.location && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PlaceIcon sx={{ fontSize: '1.1rem', color: 'primary.main', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">
                                {formatLocation(event.location, event)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                {/* LiveBarn Link (if applicable) */}
                {getLiveBarnLink(event.location, event.start) && (
                    <Box sx={{ mt: 1.5 }}>
                        {getLiveBarnLink(event.location, event.start)}
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Action Footer */}
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {event.url && event.isTournament && (
                    <Button
                        component={RouterLink}
                        to={event.url}
                        variant="outlined"
                        size="small"
                        onClick={() => logEvent('click_tournament_details_next_duty', { url: event.url })}
                    >
                        Tournament Details
                    </Button>
                )}
                <Button
                    variant="outlined"
                    size="small"
                    component={RouterLink}
                    to="/volunteers"
                    onClick={() => logEvent('navigate', { to: 'Volunteers', from: 'home_next_duty' })}
                >
                    View All Duties
                </Button>
            </Box>
        </Paper>
    );
};

export default NextDutyCard;
